import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getSurfaceBorder, getSurfaceColor, radii } from '../../theme/tokens';
import { DecisionBadge } from './DecisionBadge';
import { ProfileImage } from './ProfileImage';
import { useEvidenceImage } from './useEvidenceImage';
import { buildProfileUrl, formatFollowerCount, formatNetwork } from './formatters';
import { buildWhyExplanation, labelForSignal } from './explanation';
import type { UiCandidate } from './types';

export const CandidateCard = ({
  candidate,
  onViewEvidence,
}: {
  candidate: UiCandidate;
  onViewEvidence: () => void;
}) => {
  const theme = useTheme();
  const { profile, decision, explanationSignals, evidence } = candidate;
  const followers = profile ? formatFollowerCount(profile.followerCount) : null;
  const profileUrl = profile ? buildProfileUrl(profile.network, profile.handle) : null;
  const whySummary = evidence
    ? buildWhyExplanation(evidence)
    : 'No evaluation is available yet for this candidate.';
  // The EXACT target image THIS evaluation's image comparison analyzed —
  // never the live social profile picture. Anything other than 'available'
  // (unavailable OR error -- the card keeps this compact, no error text)
  // falls through to ProfileImage's generic icon below, never to initials or
  // profile.imageUrl.
  const { objectUrl: targetEvidenceImageUrl } = useEvidenceImage(evidence?.evaluationId, 'target');

  return (
    <Box
      sx={{
        backgroundColor: getSurfaceColor(theme, 'raised'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.lg}px`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        height: '100%',
        p: 2,
      }}
    >
      <Stack alignItems="flex-start" direction="row" spacing={1.25}>
        <ProfileImage
          imageUrl={targetEvidenceImageUrl}
          displayName={profile?.displayName ?? null}
          handle={profile?.handle ?? null}
          size={44}
          genericFallback
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 15, fontWeight: 700 }}>
            {profile?.displayName ?? profile?.handle ?? 'Unresolved profile'}
          </Typography>
          <Stack alignItems="center" direction="row" spacing={0.75}>
            {profile?.handle && (
              <Typography noWrap sx={{ color: theme.palette.text.secondary, fontSize: 12.5 }}>
                @{profile.handle}
              </Typography>
            )}
            {profile && (
              <Chip
                label={formatNetwork(profile.network)}
                size="small"
                sx={{
                  backgroundColor: getSurfaceColor(theme, 'subtle'),
                  fontSize: 10.5,
                  fontWeight: 600,
                  height: 18,
                }}
              />
            )}
          </Stack>
          {profileUrl && (
            <Link
              href={profileUrl}
              rel="noopener noreferrer"
              target="_blank"
              sx={{ alignItems: 'center', display: 'inline-flex', fontSize: 11.5, fontWeight: 600, gap: 0.3 }}
            >
              View profile <OpenInNewIcon sx={{ fontSize: 11 }} />
            </Link>
          )}
          {followers && (
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12 }}>
              {followers} followers
            </Typography>
          )}
        </Box>
      </Stack>

      <DecisionBadge decision={decision} imageStatus={evidence?.imageEvidence.status} />

      <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12.5, lineHeight: 1.4 }}>
        {whySummary}
      </Typography>

      {explanationSignals.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', color: theme.palette.text.secondary, mb: 0.5 }}>
            WHY
          </Typography>
          <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap>
            {explanationSignals.map(signal => (
              <Chip
                key={signal.name}
                label={labelForSignal(signal.name)}
                size="small"
                sx={{
                  backgroundColor:
                    signal.polarity === 'supports'
                      ? alpha(theme.palette.success.main, 0.1)
                      : signal.polarity === 'contradicts'
                        ? alpha(theme.palette.error.main, 0.1)
                        : getSurfaceColor(theme, 'subtle'),
                  color:
                    signal.polarity === 'supports'
                      ? theme.palette.success.main
                      : signal.polarity === 'contradicts'
                        ? theme.palette.error.main
                        : theme.palette.text.secondary,
                  fontSize: 11,
                  height: 20,
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 'auto', pt: 1 }}>
        <Stack spacing={0}>
          {evidence && (
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
              Final match score: {(evidence.combinedScore ?? evidence.metadataScore).toFixed(2)}
            </Typography>
          )}
        </Stack>
        <Button onClick={onViewEvidence} size="small" sx={{ fontSize: 12 }}>
          View evidence →
        </Button>
      </Stack>
    </Box>
  );
};
